import React from 'react';
import { AlertTriangle, MapPin, Plus, RefreshCw, Shield, Trash2 } from 'lucide-react';

import { formatIST } from '@/lib/utils';
import { SelectField } from '@/components/phone/FieldControls';
import { Card, CardAction, EmptyState, PageHeader, PhonePage, PillButton, ScreenLoader } from '@/components/phone/primitives';
import { Colors } from '@/components/phone/theme';

/**
 * The office-only rule, on the admin's phone.
 *
 * The desktop is two one-line panes over two grids. Here it is four cards in
 * the order the question is asked: where am I, what is the rule, who is let
 * in, who was turned away. The mode opens as a sheet, and every network and
 * refusal is a card rather than a row that had to hide half its columns.
 */
export default function PhoneNetworkAccess({
  policy, refusals, loading, saving, mode, modes, modeBlurb,
  address, edgeLooksWrong, suggested, alreadyListed,
  onSetMode, onAddSuggested, onAdd, onRefresh, onRemove,
}) {
  const entries = policy?.entries || [];

  return (
    <PhonePage testID="network-access-page">
      <PageHeader
        title="Network Access"
        subtitle="Which networks the software may be used from"
        action={(
          <PillButton onClick={onAdd} testID="add-network-btn">
            <Plus className="h-4 w-4" /> Add
          </PillButton>
        )}
      />

      <div className="flex-1 px-5 pb-8">
        {loading && !policy ? <ScreenLoader /> : (
          <>
            <Card title="This device" testID="this-device-card">
              <p className="flex items-start gap-2 text-[13px]" style={{ color: Colors.text.secondary }}>
                <MapPin className="mt-0.5 h-4 w-4 shrink-0" style={{ color: Colors.text.tertiary }} />
                <span>
                  The server sees this device as{' '}
                  <span className="font-mono font-semibold" style={{ color: Colors.text.primary }}>{address || 'unknown'}</span>
                </span>
              </p>
              {suggested && !alreadyListed && (
                <CardAction label={`Add ${suggested}`} tone="accent" onClick={onAddSuggested} className="mt-3" testID="add-this-device-btn" />
              )}
              {alreadyListed && (
                <p className="mt-2 text-[12px] font-medium" style={{ color: Colors.semantic.positive }}>already on the list</p>
              )}
              {edgeLooksWrong && (
                <div
                  className="mt-3 flex items-start gap-2 rounded-[10px] border p-3 text-[12px]"
                  style={{ backgroundColor: Colors.semantic.cautionWash, borderColor: '#E8DCC4', color: Colors.semantic.caution }}
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    That is a Cloudflare address, not this device's. The edge is not passing the real
                    client through, so every account would be refused. Fix the nginx <code>real_ip</code>
                    {' '}configuration in <code>docs/DEPLOYMENT.md</code> before switching to Enforce.
                  </span>
                </div>
              )}
            </Card>

            <Card title="Mode" className="mt-3">
              <SelectField
                name="mode"
                label="Network access mode"
                value={mode}
                options={modes.map((m) => ({ value: m.value, label: m.label }))}
                onChange={(value) => { if (!saving) onSetMode(value); }}
              />
              <p className="mt-2 text-[12px]" style={{ color: Colors.text.secondary }}>{modeBlurb}</p>
            </Card>

            <Card
              title="Allowed networks"
              aside={`${entries.length} entr${entries.length === 1 ? 'y' : 'ies'}`}
              className="mt-3"
              testID="allowlist-card"
            >
              {entries.length === 0 ? (
                <EmptyState icon={Shield} message="No networks yet" hint="Nobody is refused until at least one is added." />
              ) : (
                <ul>
                  {entries.map((entry) => (
                    <li
                      key={entry.id}
                      data-testid={`network-card-${entry.id}`}
                      className="flex items-center gap-3 border-b py-3 last:border-b-0"
                      style={{ borderColor: Colors.border.subtle }}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[14px] font-semibold" style={{ color: Colors.text.primary }}>
                          {entry.label || '—'}
                        </span>
                        <span className="block truncate font-mono text-[12px]" style={{ color: Colors.text.secondary }}>{entry.cidr}</span>
                        <span className="block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
                          {entry.added_by || '—'}{entry.added_at ? ` · ${entry.added_at.slice(0, 10)}` : ''}
                        </span>
                      </span>
                      <CardAction icon={Trash2} label="Remove" tone="critical" onClick={() => onRemove(entry)}
                        testID={`network-remove-${entry.id}`} />
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card
              title={mode === 'enforce' ? 'Refused' : 'Would be refused'}
              aside={mode === 'monitor' ? 'recorded, not blocked' : undefined}
              className="mt-3"
              testID="refusals-card"
            >
              {refusals.length === 0 ? (
                <p className="py-4 text-center text-[13px]" style={{ color: Colors.text.tertiary }}>Nothing recorded</p>
              ) : (
                <ul>
                  {refusals.map((r) => (
                    <li
                      key={r.id}
                      data-testid={`refusal-card-${r.id}`}
                      className="border-b py-3 last:border-b-0"
                      style={{ borderColor: Colors.border.subtle }}
                    >
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-[14px] font-semibold" style={{ color: Colors.text.primary }}>
                          {r.user_name || r.user_id}
                        </span>
                        <span className="shrink-0 text-[12px] tabular-nums" style={{ color: Colors.text.secondary }}>
                          {r.count} request{r.count === 1 ? '' : 's'}
                        </span>
                      </span>
                      <span className="block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>{(r.roles || []).join(', ')}</span>
                      <span className="mt-1 block truncate font-mono text-[12px]" style={{ color: Colors.text.secondary }}>{r.address}</span>
                      <span className="block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
                        last {formatIST(r.last_seen, { compact: true })}{r.last_path ? ` · ${r.last_path}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <button
              type="button"
              onClick={onRefresh}
              className="mx-auto mt-4 flex h-11 items-center gap-1 px-4 text-[13px] font-semibold"
              style={{ color: Colors.accent.DEFAULT }}
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
          </>
        )}
      </div>
    </PhonePage>
  );
}
